/**
 * Figaro — Lease return / delivery conditions.
 * Routes: #/figaro/lease-returns, #/figaro/lease-returns/new, #/figaro/lease-returns/:id
 */

import {
  SECTION_GROUP_LABELS,
  STD_KEY_ORDER,
  snapToBullets,
  sentenceLine,
  foldRows,
  injectCommercialRows,
  canonicalGroup,
  shortIdent,
  fmtDateTime,
  formatMoneyInText,
  hygienizeSnap,
} from "./lease-return-format.js";
import { openPassageViewer } from "./lease-return-pdf-viewer.js";
import { computeSettlement, fmtMoney } from "./lease-return-trueup.js";

/** Accumulated AI usage shown without dollars: query + result tokens, compact. */
function fmtTokenCount(v) {
  const n = Number(v) || 0;
  if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M tokens`;
  if (n >= 1e3) return `${Math.round(n / 1e3)}k tokens`;
  return `${n} tokens`;
}

const STYLE_ID = "lease-returns-styles-v10";

function ensureStyles() {
  for (const id of [
    "lease-returns-styles",
    "lease-returns-styles-v2",
    "lease-returns-styles-v3",
    "lease-returns-styles-v4",
    "lease-returns-styles-v5",
    "lease-returns-styles-v6",
    "lease-returns-styles-v7",
    "lease-returns-styles-v8",
    "lease-returns-styles-v9",
  ]) {
    const old = document.getElementById(id);
    if (old) old.remove();
  }
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
.lr-card { background:#fff; border:1px solid #e5e7eb; border-radius:.75rem; box-shadow:0 1px 2px rgba(16,24,40,.04); }
.lr-table { width:100%; border-collapse:collapse; font-size:.8125rem; background:#fff; }
.lr-table th { text-align:left; color:#fff; font-weight:600; padding:.55rem .7rem; border-bottom:none; white-space:nowrap; background:#b91c1c; font-size:.68rem; letter-spacing:.05em; text-transform:uppercase; }
.lr-table td { padding:.5rem .7rem; border-bottom:1px solid #e5e7eb; vertical-align:top; color:#111827; background:transparent; }
.lr-status { display:inline-flex; padding:.12rem .5rem; border-radius:.35rem; font-size:.7rem; font-weight:600; border:1px solid #e5e7eb; background:#f9fafb; color:#374151; }
.lr-actionbar {
  background: #fff; border: 1px solid #e5e7eb; border-radius: .75rem;
  box-shadow: 0 1px 2px rgba(16,24,40,.04);
  padding: .65rem 1rem; margin-bottom: 1rem;
}
.lr-actionbar-inner {
  display: flex; flex-wrap: wrap;
  align-items: center; justify-content: space-between; gap: .75rem;
}
.lr-detail-body { padding-top: 0; }
.lr-light { display:flex; align-items:flex-start; gap:.55rem; font-size:.78rem; }
.lr-flag { font-size:.65rem; font-weight:700; letter-spacing:.04em; text-transform:uppercase; color:#6b7280; min-width:2.75rem; padding-top:.15rem; flex-shrink:0; }
.lr-flag-need { color:#a16207; }
.lr-flag-warn { color:#a16207; }
.lr-flag-ok { color:#047857; }
.lr-flag-opt { color:#2563eb; }
.lr-plus { display:inline-flex; align-items:center; justify-content:center; width:1.75rem; height:1.75rem; border-radius:.5rem; border:1px solid #e5e7eb; background:#fff; color:#b91c1c; font-size:1.15rem; line-height:1; cursor:pointer; }
.lr-plus:hover { background:#fef2f2; border-color:#fecaca; }
.lr-icon { display:inline-flex; align-items:center; justify-content:center; width:1.75rem; height:1.75rem; border-radius:.45rem; border:1px solid #e5e7eb; background:#fff; color:#374151; font-size:.85rem; cursor:pointer; text-decoration:none; }
.lr-icon:hover { background:#f9fafb; }
.lr-icon-danger { color:#b91c1c; }
.lr-drop { border:2px dashed #d1d5db; border-radius:.75rem; padding:1rem; text-align:center; color:#6b7280; font-size:.8125rem; cursor:pointer; }
.lr-drop:hover, .lr-drop.is-drag { border-color:#b91c1c; background:rgba(185,28,28,.04); }
.lr-section-h { font-size:.7rem; letter-spacing:.06em; text-transform:uppercase; color:#9ca3af; font-weight:700; margin:1rem 0 .35rem; }
.lr-snap { font-size:.8125rem; color:#111827; }
.lr-detail { font-size:.75rem; color:#6b7280; margin-top:.2rem; }
.lr-kv { display:grid; grid-template-columns: 11rem 1fr; gap:.35rem .75rem; font-size:.8125rem; }
.lr-kv dt { color:#6b7280; }
.lr-kv dd { color:#111827; margin:0; }
.lr-pulse { display:inline-flex; align-items:center; gap:.35rem; }
.lr-pulse-dot { width:.45rem; height:.45rem; border-radius:999px; background:#f59e0b; animation:lr-pulse 1.1s ease-in-out infinite; }
@keyframes lr-pulse { 0%,100%{ opacity:.35; transform:scale(.85);} 50%{ opacity:1; transform:scale(1.15);} }
.lr-modal-backdrop { position:fixed; inset:0; background:rgba(17,24,39,.5); z-index:90; display:flex; align-items:center; justify-content:center; padding:1rem; }
.lr-modal { background:#fff; border-radius:1rem; width:min(32rem,100%); box-shadow:0 24px 60px rgba(0,0,0,.22); overflow:hidden; }
.lr-modal-h { padding:1.1rem 1.25rem .25rem; font-weight:650; color:#111827; font-size:1.05rem; }
.lr-modal-b { padding:.5rem 1.25rem 1.35rem; }
.lr-progress-track { height:.5rem; background:#f3f4f6; border-radius:999px; overflow:hidden; }
.lr-progress-bar { height:100%; width:30%; background:linear-gradient(90deg,#b91c1c,#ea580c,#b91c1c); background-size:200% 100%; animation:lr-indeterminate 1.15s linear infinite; border-radius:999px; }
.lr-progress-bar.is-pct { animation:none; background:#b91c1c; transition:width .35s ease; }
@keyframes lr-indeterminate { 0%{ background-position:100% 0;} 100%{ background-position:-100% 0;} }
.lr-step { font-size:.9rem; color:#111827; min-height:1.4rem; font-weight:560; margin-top:.55rem; }
.lr-elapsed { font-size:.78rem; color:#6b7280; font-variant-numeric: tabular-nums; margin-top:.25rem; }
.lr-step-log { max-height:11rem; overflow:auto; font-size:.75rem; color:#4b5563; margin-top:.85rem; border-top:1px solid #f3f4f6; padding-top:.55rem; }
.lr-step-log div { padding:.25rem 0; border-bottom:1px solid #f9fafb; }
.lr-snap-matrix td { font-size:.78rem; padding:.5rem .7rem; white-space:normal; background:transparent; }
.lr-snap-matrix td:first-child { min-width:10rem; font-weight:560; color:#111827; }
.lr-snap-matrix .lr-align { font-size:.68rem; font-weight:650; letter-spacing:.04em; text-transform:uppercase; color:#374151; white-space:nowrap; }
.lr-snap-matrix .lr-align-same { color:#047857; }
.lr-snap-matrix .lr-align-gap { color:#b45309; }
.lr-snap-matrix .lr-align-silent { color:#9ca3af; }
.lr-row-silent td { color:#9ca3af; }
.lr-silent-toggle { cursor:pointer; color:#6b7280; font-size:.72rem; font-weight:600; padding:.4rem .7rem; background:#f9fafb; }
.lr-silent-toggle:hover { color:#111827; background:#f3f4f6; }
.lr-settle { margin:0 0 1rem; }
.lr-settle h2 { font-size:.7rem; letter-spacing:.06em; text-transform:uppercase; color:#6b7280; font-weight:700; margin:0 0 .45rem; }
.lr-settle-warn { background:#fffbeb; border:1px solid #fcd34d; border-radius:.5rem; padding:.55rem .75rem; font-size:.78rem; color:#92400e; margin:0 0 .65rem; }
.lr-settle-warn li { margin:.15rem 0; }
.lr-settle .lr-miss { color:#b91c1c; font-weight:650; }
.lr-settle .lr-net { font-weight:700; font-size:.9rem; }
/* td selector below must beat ".lr-snap-matrix td { white-space:normal }" or bullets collapse to one line */
.lr-snap-cell, .lr-snap-matrix td.lr-snap-cell { white-space:pre-line; line-height:1.45; font-variant-numeric:tabular-nums; }
.lr-note { white-space:pre-line; font-size:.72rem; color:#6b7280; line-height:1.4; }
.lr-row-section td { background:#e5e7eb; color:#111827; font-size:.68rem; font-weight:700; letter-spacing:.06em; text-transform:uppercase; border-top:none; border-bottom:1px solid #d1d5db; padding:.55rem .7rem; }
.lr-snap-block { background:#f9fafb; border:1px solid #e5e7eb; border-radius:.65rem; padding:.85rem 1rem; }
.lr-snap-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(11rem,1fr)); gap:.55rem .85rem; font-size:.8125rem; }
.lr-snap-grid dt { color:#6b7280; font-size:.68rem; letter-spacing:.04em; text-transform:uppercase; }
.lr-snap-grid dd { margin:0; color:#111827; font-weight:650; }
.lr-pdf-backdrop { position:fixed; inset:0; background:rgba(17,24,39,.55); z-index:95; display:flex; align-items:center; justify-content:center; padding:1rem; }
.lr-pdf-modal { background:#fff; border-radius:.85rem; width:min(72rem,100%); max-height:92vh; overflow:auto; box-shadow:0 24px 60px rgba(0,0,0,.28); }
.lr-pdf-head { display:flex; align-items:flex-start; justify-content:space-between; gap:.75rem; padding:.85rem 1rem; border-bottom:1px solid #e5e7eb; }
.lr-pdf-title { font-weight:650; color:#111827; font-size:.95rem; }
.lr-pdf-chip { font-size:.65rem; font-weight:700; letter-spacing:.04em; text-transform:uppercase; border-radius:.3rem; padding:.1rem .4rem; color:#111827; }
.lr-pdf-status { font-size:.75rem; color:#6b7280; padding:.35rem 1rem 0; }
.lr-pdf-grid { display:grid; grid-template-columns:minmax(0,1.15fr) minmax(0,1fr); gap:.75rem; padding:.75rem 1rem 1rem; }
@media (max-width: 900px) { .lr-pdf-grid { grid-template-columns:1fr; } }
.lr-pdf-pane { border:1px solid #e5e7eb; border-radius:.5rem; overflow:hidden; background:#fff; }
.lr-pdf-pane-h { font-size:.65rem; letter-spacing:.06em; text-transform:uppercase; color:#6b7280; font-weight:700; padding:.4rem .7rem; background:#f3f4f6; border-bottom:1px solid #e5e7eb; }
.lr-pdf-stage { position:relative; background:#f3f4f6; min-height:16rem; }
.lr-pdf-canvas, .lr-pdf-overlay { display:block; width:100%; height:auto; }
.lr-pdf-overlay { position:absolute; left:0; top:0; pointer-events:none; }
.lr-pdf-iframe { width:100%; height:28rem; border:0; background:#fff; }
.lr-pdf-text { font-size:.78rem; white-space:pre-wrap; font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace; line-height:1.5; color:#111827; max-height:28rem; overflow:auto; padding:.75rem; background:#f9fafb; }
.lr-super h2 { font-size:.7rem; letter-spacing:.06em; text-transform:uppercase; color:#6b7280; font-weight:700; margin:1.25rem 0 .4rem; }
.lr-super p { font-size:.84rem; color:#111827; line-height:1.5; margin:0 0 .65rem; }
.lr-chip { display:inline-flex; flex-direction:column; gap:.1rem; min-width:7rem; }
.lr-chip span:first-child { font-size:.65rem; letter-spacing:.05em; text-transform:uppercase; color:#9ca3af; }
.lr-chip span:last-child { font-size:.84rem; font-weight:650; color:#111827; white-space:pre-line; }
.lr-step-log .is-current { font-weight:650; color:#111827; }
.lr-report-page { max-width:72rem; margin:0 auto; padding:1rem 1.25rem 2rem; }
.lr-report-page h1 { font-size:1.15rem; font-weight:700; color:#111827; margin:0 0 .25rem; }
.lr-report-meta { font-size:.78rem; color:#6b7280; margin-bottom:.65rem; }
.lr-disclaimer { font-size:.8125rem; color:#4b5563; line-height:1.45; margin:0 0 .85rem; }
.lr-docs-used { font-size:.78rem; color:#111827; margin:1.25rem 0 0; }
.lr-docs-used h2 { font-size:.68rem; letter-spacing:.06em; text-transform:uppercase; color:#6b7280; font-weight:700; margin:0 0 .35rem; }
.lr-stage-rail { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:.5rem; margin-bottom:1rem; }
.lr-stage-btn { border:1px solid #e5e7eb; cursor:pointer; text-align:left; background:#fff; border-radius:.65rem; padding:.55rem .7rem; }
.lr-stage-btn:hover { background:#f9fafb; }
.lr-stage-btn.is-on { border-color:#111827; box-shadow:0 0 0 1px #111827; }
.lr-stage-letter { font-size:.65rem; font-weight:700; letter-spacing:.06em; color:#6b7280; }
.lr-stage-title { font-size:.82rem; font-weight:700; color:#111827; }
.lr-stage-hint { font-size:.68rem; color:#6b7280; margin-top:.1rem; }
.lr-ready-ok { color:#047857; font-weight:650; }
.lr-ready-need { color:#a16207; font-weight:650; }
.lr-ready-opt { color:#2563eb; font-weight:650; }
.lr-doc-progress { min-width:11rem; }
.lr-doc-pct { font-size:1.2rem; font-weight:750; color:#047857; font-variant-numeric:tabular-nums; line-height:1.1; }
.lr-doc-progress-label { font-size:.72rem; color:#374151; margin-top:.2rem; font-variant-numeric:tabular-nums; }
.lr-doc-progress .lr-progress-track { height:.7rem; margin-top:.3rem; }
.lr-doc-bar { height:100%; background:#059669; border-radius:999px; transition:width .35s ease; }
.lr-doc-bar.is-indeterminate { width:30%; background:linear-gradient(90deg,#059669,#34d399,#059669); background-size:200% 100%; animation:lr-indeterminate 1.15s linear infinite; }
.lr-parse-banner { border:1px solid #a7f3d0; background:#ecfdf5; border-radius:.65rem; padding:.7rem .9rem; }
.lr-traps { font-size:.8rem; color:#92400e; background:#fffbeb; border:1px solid #fde68a; border-radius:.5rem; padding:.65rem .85rem; margin-bottom:1rem; }
.lr-traps li { margin:.2rem 0; }
@media print {
  .lr-no-print, .lr-actionbar { display:none !important; }
  .lr-detail-body { padding-top:0; }
  .lr-report-page { max-width:none; padding:0; }
}
`;
  document.head.appendChild(style);
}

const MONTHS_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** All report dates render as dd-Mmm-yyyy (e.g. 16-Apr-2015). */
function fmtDateShort(v) {
  if (!v) return "—";
  const s = String(v);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  try {
    const d = iso
      ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
      : new Date(v);
    if (Number.isNaN(d.getTime())) return s;
    return `${String(d.getDate()).padStart(2, "0")}-${MONTHS_ABBR[d.getMonth()]}-${d.getFullYear()}`;
  } catch {
    return s;
  }
}

function disclaimerEl(el) {
  return el("p", {
    class: "lr-disclaimer",
    text: "This is a computer analysis. The FM should always read the documents in full and draw their own conclusions.",
  });
}

function executionLabel(d) {
  const blob = `${d?.title || ""} ${d?.originalFilename || ""} ${d?.displayTitle || ""} ${d?.kind || ""} ${d?.role || ""}`.toLowerCase();
  const isWordFile = /\.docx?\b/.test(blob);
  // Word files carry no signatures — never show Executed for them.
  if (isWordFile && !/\bsigned\b/.test(blob)) {
    return /execution version/.test(blob)
      ? "Execution version — unsigned Word copy"
      : "Might be a draft";
  }
  if (d?.executionLabel) return d.executionLabel;
  if (d?.execution === "executed" || d?.executionStatus === "executed") return "Executed";
  if (d?.execution === "draft" || d?.executionStatus === "draft") return "Might be a draft";
  const role = String(d?.kind || d?.role || "").toUpperCase();
  if (/\bsigned\b|execution version/.test(blob) && !/compare|as comments/.test(blob)) return "Executed";
  if (
    /\bunsigned\b|\bas comments\b|letter of intent|\bloi\b|compare /.test(blob) ||
    role === "NEXT_OPERATOR_LOI"
  ) {
    return "Might be a draft";
  }
  if (d?.execution === "unclear" || d?.executionStatus === "unclear") return "Execution not confirmed";
  return "Execution not confirmed";
}

function documentDateOf(d) {
  return d?.date || d?.displayDate || d?.documentDate || null;
}

function governingDocsForDisplay(report, project) {
  const byId = new Map();
  for (const d of project?.documents || []) {
    byId.set(Number(d.documentId), {
      documentId: d.documentId,
      kind: d.role,
      title: d.displayTitle || d.title || d.originalFilename,
      originalFilename: d.originalFilename,
      date: d.displayDate || d.documentDate,
      execution: d.executionStatus,
      executionLabel: d.executionLabel,
      executionNote: d.executionNote,
      role: d.role,
    });
  }
  for (const d of report?.governingDocuments || []) {
    const id = Number(d.documentId);
    const prev = byId.get(id) || {};
    byId.set(id, {
      ...prev,
      ...d,
      date: prev.date || d.date || null,
      title: d.title || prev.title,
      kind: d.kind || prev.kind,
      execution: d.execution || prev.execution,
      executionLabel: d.executionLabel || prev.executionLabel,
      executionNote: d.executionNote || prev.executionNote,
    });
  }
  return [...byId.values()];
}

function documentsUsedBlock(el, docs, { apiUrl, fetchJson } = {}) {
  if (!docs?.length) return null;
  return el("div", { class: "lr-docs-used" }, [
    el("h2", { text: "Documents (least critical — for reference)" }),
    el("div", { class: "lr-card overflow-hidden" }, [
      el("table", { class: "lr-table" }, [
        el("thead", {}, [
          el("tr", {}, [
            el("th", { text: "Kind" }),
            el("th", { text: "Name" }),
            el("th", { text: "Date" }),
            el("th", { text: "Execution" }),
            el("th", { text: "" }),
          ]),
        ]),
        el(
          "tbody",
          {},
          docs.map((d) =>
            el("tr", {}, [
              el("td", { text: roleLabel(d.kind || d.role) }),
              el("td", { text: d.title || d.displayTitle || d.originalFilename || "—" }),
              el("td", { text: fmtDateShort(documentDateOf(d)) }),
              el("td", { text: executionLabel(d) }),
              el("td", {}, [
                d.documentId && apiUrl && fetchJson
                  ? el("button", {
                      type: "button",
                      class: "lr-icon",
                      title: "Open document with highlights",
                      text: "📄",
                      onclick: () =>
                        openPassageViewer(el, {
                          documentId: d.documentId,
                          page: 1,
                          excerpt: "",
                          color: "yellow",
                          title: d.title || d.displayTitle || d.originalFilename,
                          apiUrl,
                          fetchJson,
                        }),
                    })
                  : null,
              ]),
            ])
          )
        ),
      ]),
    ]),
  ]);
}

function compareModeOf(report, project) {
  const docs = project?.documents || [];
  const hasLease = docs.some((d) => d.role === "NEXT_OPERATOR_LEASE_DRAFT");
  const hasLoi = docs.some((d) => d.role === "NEXT_OPERATOR_LOI");
  const nextPresent = (report?.nextOperatorDelivery || []).some((r) => r?.present);
  if (hasLease || (nextPresent && !hasLoi)) return "lease";
  if (hasLoi) return "loi";
  return "none";
}

function docAnalysisHref(docId, { page, q } = {}) {
  const base = `${window.location.pathname}${window.location.search}`;
  const params = new URLSearchParams();
  if (page) params.set("page", String(page));
  if (q) params.set("q", String(q).slice(0, 120));
  const qs = params.toString();
  return `${base}#/figaro/documents/${docId}${qs ? `?${qs}` : ""}`;
}

function sourceHref(source) {
  if (!source?.documentId) return null;
  const q = source.excerpt || source.quote || source.clause || "";
  return docAnalysisHref(source.documentId, {
    page: source.page ?? source.pageStart,
    q: q ? String(q).slice(0, 100) : undefined,
  });
}

function snapCell(row, { blank = "—", silent = false } = {}) {
  if (!row?.present) {
    if (silent) return "— silent —";
    return blank;
  }
  const { snap } = hygienizeSnap(row.key, row.snap || row.summary || "");
  return snapToBullets(snap) || blank;
}

function noteForRow(row, sideHint) {
  if (!row?.present) return "";
  const raw = String(row.note || "").trim();
  const { noteExtra } = hygienizeSnap(row.key, row.snap || row.summary || "");
  const bits = [];
  if (raw) bits.push(raw);
  if (noteExtra) {
    const tagged =
      sideHint === "next" && !/^Next:/i.test(noteExtra)
        ? `Next: ${noteExtra}`
        : sideHint === "return" && !/^Return:/i.test(noteExtra)
          ? `Return: ${noteExtra}`
          : noteExtra;
    if (!raw.toLowerCase().includes(noteExtra.toLowerCase().slice(0, 40))) bits.push(sentenceLine(tagged));
  }
  return bits.join("\n");
}

function figuresOf(s) {
  const found = [];
  // Must contain a digit — a bare "." (sentence period) is not a figure.
  const re = /(\$?\d[\d,.]*)\s*(FH|FC|DY|YR|MO|%|TSLSV|TSLPR|CLSV|OVH)?/gi;
  let m;
  const t = String(s || "");
  while ((m = re.exec(t))) {
    // "5,000" and "5000" are the same value; trailing sentence period is not decimal.
    const num = m[1].replace(/,/g, "").replace(/\.+$/, "");
    found.push(`${num}${m[2] ? ` ${m[2].toUpperCase()}` : ""}`.toLowerCase());
  }
  return found.sort().join("|");
}

function snapsLookSame(a, b) {
  const sa = String(a || "").trim();
  const sb = String(b || "").trim();
  if (!sa || !sb || sa === "Not found" || sb === "Not found") return false;
  const compact = (s) => s.toLowerCase().replace(/[\s/\n]+/g, " ").trim();
  if (compact(sa) === compact(sb)) return true;
  const fa = figuresOf(sa);
  const fb = figuresOf(sb);
  if (fa && fb) return fa === fb;
  if (fa || fb) return false;
  const syn = {
    airworthy: "airworthy",
    airworthiness: "airworthy",
    serviceable: "airworthy",
    serviceability: "airworthy",
    clean: "airworthy",
    waiver: "deferred",
    waivers: "deferred",
    deferred: "deferred",
    mel: "deferred",
    cdl: "deferred",
    lessor: "lessor",
    owner: "lessor",
    lessee: "lessee",
    operator: "lessee",
  };
  const tok = (s) =>
    new Set(
      compact(s)
        .replace(/[^a-z0-9%\s]/g, " ")
        .split(/\s+/)
        .map((w) => syn[w] || w)
        .filter((w) => w.length > 2 && !/^\d/.test(w))
    );
  const qa = tok(sa);
  const qb = tok(sb);
  if (!qa.size || !qb.size) return false;
  let hit = 0;
  for (const t of qa) if (qb.has(t)) hit += 1;
  return hit / Math.min(qa.size, qb.size) >= 0.45;
}

function stripNotePrefix(n) {
  return String(n || "").replace(/^(Return|Next)\s*:\s*/i, "").trim();
}

function noteRepeatsSnap(note, snap) {
  const n = stripNotePrefix(note).toLowerCase().replace(/\s+/g, " ");
  const s = String(snap || "")
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!n) return true;
  if (!s || s === "not found") return false;
  return n === s || s.includes(n) || (n.length < 90 && n.includes(s));
}

function combinedNote(cur, nxt, hasNext) {
  const cr = noteForRow(cur, "return");
  const nr = noteForRow(nxt, "next");
  const cUseful = cr && cur?.present && !noteRepeatsSnap(cr, snapCell(cur));
  const nUseful = nr && nxt?.present && !noteRepeatsSnap(nr, snapCell(nxt));
  if (!hasNext) return cUseful ? sentenceLine(cr) : "";
  if (cUseful && nUseful) {
    if (stripNotePrefix(cr).toLowerCase() === stripNotePrefix(nr).toLowerCase()) {
      return sentenceLine(stripNotePrefix(cr));
    }
    return `Return: ${sentenceLine(stripNotePrefix(cr))}\nNext: ${sentenceLine(stripNotePrefix(nr))}`;
  }
  if (cUseful) return `Return: ${sentenceLine(stripNotePrefix(cr))}`;
  if (nUseful) return `Next: ${sentenceLine(stripNotePrefix(nr))}`;
  return "";
}

function docFileHref(apiUrl, docId) {
  return apiUrl(`/api/ai/documents/${docId}/file`);
}

function roleLabel(role) {
  const map = {
    LEASE: "Lease",
    LEASE_AMENDMENT: "Amendment",
    OTHER_LEGAL: "Other legal",
    NEXT_OPERATOR_LOI: "Next op. LOI",
    NEXT_OPERATOR_LEASE_DRAFT: "Next op. lease draft",
    SUPPORTING: "Supporting",
  };
  return map[role] || role || "—";
}

function assetLabel(a) {
  const msn = a.serialNumber || a.msn || "";
  const reg = a.metadata?.registration || a.registration || "";
  const model = a.model || "";
  const op = a.operatorName || "";
  return [msn && `MSN ${msn}`, reg, model, op].filter(Boolean).join(" · ");
}

function isDocProcessing(d) {
  const st = String(d.status || "").toUpperCase();
  return ["UPLOADED", "PARSING", "OCR", "OCR_PENDING", "CHUNKING", "EMBEDDING", "INDEXING"].includes(st);
}

function parseProgressOf(d) {
  if (!isDocProcessing(d)) return null;
  const total = Number(d.pageCount || 0);
  const q = d.ocrQuality != null ? Number(d.ocrQuality) : null;
  if (total > 0 && q != null && Number.isFinite(q) && q >= 0) {
    const pct = Math.min(1, Math.max(0, q));
    const current = Math.max(0, Math.min(total, Math.round(pct * total)));
    return { current, total, pct };
  }
  if (total > 0) return { current: 0, total, pct: 0 };
  return { current: 0, total: 0, pct: null };
}

function etaFromPages(current, total) {
  if (!current || !total || current < 2) return "";
  const sec = Math.max(0, Math.round((total - current) * 5));
  if (sec < 25) return "less than a minute left";
  if (sec < 90) return "about 1 min left";
  return `about ${Math.max(1, Math.round(sec / 60))} min left`;
}

function parseProgressLabel(prog) {
  if (!prog) return "Parsing…";
  if (prog.total > 0 && prog.current > 0) {
    const eta = etaFromPages(prog.current, prog.total);
    return [`Page ${prog.current} of ${prog.total}`, eta].filter(Boolean).join(" · ");
  }
  if (prog.total > 0) return `Preparing ${prog.total} pages…`;
  return "Parsing scanned pages — this can take up to 20 minutes";
}

function ingestBar(el, d) {
  const prog = parseProgressOf(d);
  if (!prog) return null;
  const shown = prog.pct == null ? null : Math.round(prog.pct * 100);
  const width = shown == null ? null : Math.max(3, shown);
  return el("div", { class: "lr-doc-progress" }, [
    shown != null
      ? el("div", { class: "lr-doc-pct", text: `${shown}%` })
      : el("div", { class: "lr-doc-pct", style: "color:#a16207", text: "…" }),
    el("div", { class: "lr-progress-track" }, [
      el("div", {
        class: width == null ? "lr-doc-bar is-indeterminate" : "lr-doc-bar",
        style: width == null ? "" : `width:${width}%`,
      }),
    ]),
    el("div", { class: "lr-doc-progress-label", text: parseProgressLabel(prog) }),
  ]);
}

function parsingBanner(el, docs) {
  const busy = (docs || []).filter(isDocProcessing);
  if (!busy.length) return null;
  const parts = busy.map((d) => {
    const prog = parseProgressOf(d);
    const name = d.displayTitle || d.title || d.originalFilename || "Document";
    const shown = prog?.pct != null ? `${Math.round(prog.pct * 100)}%` : null;
    const pages =
      prog?.total > 0 && prog.current > 0 ? `page ${prog.current} of ${prog.total}` : null;
    const eta = prog ? etaFromPages(prog.current, prog.total) : "";
    return [name, shown, pages, eta].filter(Boolean).join(" — ");
  });
  return el("div", { class: "lr-parse-banner" }, [
    el("div", { class: "text-sm font-semibold text-gray-900", text: "Indexing in progress" }),
    el("div", {
      class: "text-sm text-gray-700 mt-0.5",
      text: `${parts.join(" · ")}. Scanned PDFs can take up to 20 minutes. You can leave this page.`,
    }),
  ]);
}

function statusLabel(d) {
  if (isDocProcessing(d)) return parseProgressLabel(parseProgressOf(d));
  const st = d.status || "—";
  const bits = [st];
  if (d.chunkCount != null) bits.push(`${d.chunkCount} chunks`);
  if (d.pageCount != null && d.pageCount > 0) bits.push(`${d.pageCount}p`);
  if (d.ocrStatus && d.ocrStatus !== "NATIVE_TEXT") bits.push(d.ocrStatus.replace(/_/g, " ").toLowerCase());
  if (d.ocrQuality != null && Number(d.ocrQuality) < 0.5) bits.push(`OCR ${d.ocrQuality}`);
  return bits.join(" · ");
}

function readinessTone(c) {
  if (!c?.ok || c.warn) return { label: "Needs action", cls: "lr-ready-need" };
  const settled = /^(Found:|Confirmed:)/i.test(String(c.detail || ""));
  if (c.optional && !settled) return { label: "Optional", cls: "lr-ready-opt" };
  return { label: "Ready", cls: "lr-ready-ok" };
}

function isOcrWeak(d) {
  const ocr = String(d.ocrStatus || "").toUpperCase();
  return ocr === "OCR_WEAK" || ocr === "OCR_EMPTY" || ocr === "NEEDS_OCR";
}

function reportMethodBanner(el, report) {
  if (!report) return null;
  if (report.extractIncomplete) {
    return el("p", {
      class: "text-sm text-gray-600",
      text: report.extractIncompleteReason || "Latest run did not finish. Showing the last completed analysis.",
    });
  }
  const method = String(report.method || "");
  if (method === "ai" || !method) return null;
  if (method === "ai_parse_failed" || method === "no_ai_key") {
    return el("p", {
      class: "text-sm text-gray-600",
      text: "Analysis did not finish. Re-run it from Setup when ready.",
    });
  }
  return null;
}

function snapFieldDisplay(field, fallbackFlat) {
  if (field && typeof field === "object" && ("statedInLease" in field || "value" in field || "fleetDb" in field)) {
    const raw = field.value || field.fleetDb || fallbackFlat;
    return shortIdent(raw, field.fleetDb || fallbackFlat);
  }
  return shortIdent(fallbackFlat, null);
}

/** Type + serial in one cell — "V2527-A5 · SN V18120" — instead of type only. */
function identWithSerial(field, fallbackFlat) {
  const main = snapFieldDisplay(field, fallbackFlat);
  const serial =
    field && typeof field === "object" ? String(field.fleetDb || "").trim() : "";
  if (
    serial &&
    main !== "—" &&
    serial.toLowerCase() !== main.toLowerCase() &&
    !main.toLowerCase().includes(serial.toLowerCase())
  ) {
    return `${main} · SN ${serial}`;
  }
  return main;
}

function aircraftSnapshotBlock(el, { project, report }) {
  const snap = project?.aircraftSnapshot || {};
  const display = snap.display || {};
  const basics = report?.aircraftBasics || display;
  const kv = (k, v) => [el("dt", { text: k }), el("dd", { text: v && v !== "—" ? v : "—" })];
  const e1 = identWithSerial(snap.engines?.eng1, basics.engines?.eng1);
  const e2 = identWithSerial(snap.engines?.eng2, basics.engines?.eng2);
  const nlg = identWithSerial(snap.landingGear?.nlg, basics.landingGear?.nlg);
  const lh = identWithSerial(snap.landingGear?.lh, basics.landingGear?.lh);
  const rh = identWithSerial(snap.landingGear?.rh, basics.landingGear?.rh);
  const apu = identWithSerial(snap.apu, basics.apu);
  const pLh = identWithSerial(snap.propellers?.lh, basics.propellers?.lh);
  const pRh = identWithSerial(snap.propellers?.rh, basics.propellers?.rh);
  const hasProps = pLh !== "—" || pRh !== "—";
  const lgLine = [
    nlg !== "—" ? `NLG ${nlg}` : null,
    lh !== "—" ? `LH ${lh}` : null,
    rh !== "—" ? `RH ${rh}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return el("div", { class: "lr-snap-block mb-3" }, [
    el("div", {
      class: "text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2",
      text: "Aircraft",
    }),
    el("dl", { class: "lr-snap-grid" }, [
      ...kv("MSN", snapFieldDisplay(snap.msn, basics.msn || project?.msn)),
      ...kv("Registration", snapFieldDisplay(snap.registration, basics.registration || project?.registration)),
      ...kv("Model", snapFieldDisplay(snap.model, basics.model)),
      ...kv("Manufacturer", snapFieldDisplay(snap.manufacturer, basics.manufacturer)),
      ...kv("Engine 1", e1),
      ...kv("Engine 2", e2),
      ...kv("APU", apu),
      ...(hasProps ? [...kv("Prop LH", pLh), ...kv("Prop RH", pRh)] : []),
    ]),
    el("dl", { class: "lr-kv mt-2" }, [
      el("dt", { text: "Landing gear" }),
      el("dd", { text: lgLine || "—" }),
    ]),
  ]);
}

function chrome(el, { title, crumbs = [], actions = [] }) {
  return el("div", { class: "flex flex-wrap items-start justify-between gap-3 mb-4" }, [
    el("div", { class: "space-y-1" }, [
      el("div", { class: "flex flex-wrap items-center gap-2 text-sm text-gray-500" }, [
        el("a", { href: "#/figaro", class: "text-dae hover:underline", text: "Figaro" }),
        el("span", { text: "/" }),
        el("a", { href: "#/figaro/lease-returns", class: "text-dae hover:underline", text: "Lease return" }),
        ...crumbs.flatMap((c) => [
          el("span", { text: "/" }),
          c.href
            ? el("a", { href: c.href, class: "text-dae hover:underline", text: c.label })
            : el("span", { text: c.label }),
        ]),
      ]),
      el("h1", { class: "text-xl font-semibold text-gray-900", text: title }),
    ]),
    actions.length ? el("div", { class: "flex flex-wrap items-center gap-2" }, actions) : null,
  ]);
}

function readinessList(el, readiness, { onConfirmAmendments } = {}) {
  const checks = readiness?.checks || [];
  if (!checks.length) return el("div", { class: "text-sm text-gray-500", text: "No readiness checks yet." });
  return el(
    "ul",
    { class: "space-y-2" },
    checks.map((c) => {
      const tone = readinessTone(c);
      const flag = tone.label === "Needs action" ? "NEED" : tone.label === "Optional" ? "OPT" : "OK";
      const flagClass =
        tone.cls === "lr-ready-need"
          ? "lr-flag lr-flag-need"
          : tone.cls === "lr-ready-opt"
            ? "lr-flag lr-flag-opt"
            : "lr-flag lr-flag-ok";
      return el("li", { class: "lr-light" }, [
        el("span", { class: flagClass, text: flag }),
        el("div", { class: "min-w-0" }, [
          el("div", { class: "font-medium text-gray-800", text: c.label }),
          el("div", { class: "text-gray-500", text: c.detail || "" }),
          c.id === "amendments_visible" && c.warn && onConfirmAmendments
            ? el("button", {
                type: "button",
                class: "mt-1 text-xs text-dae hover:underline",
                text: "Confirm: no amendments exist",
                onclick: onConfirmAmendments,
              })
            : null,
        ]),
      ]);
    })
  );
}

function legalTable(el, docs, { apiUrl, fetchJson, onUnlink } = {}) {
  if (!docs?.length) {
    return el("div", { class: "text-sm text-gray-500", text: "No legal documents linked yet." });
  }
  return el("div", { class: "overflow-x-auto" }, [
    el("table", { class: "lr-table" }, [
      el("thead", {}, [
        el("tr", {}, [
          el("th", { text: "Kind" }),
          el("th", { text: "Name" }),
          el("th", { text: "Date" }),
          el("th", { text: "Execution" }),
          el("th", { text: "Status" }),
          el("th", { text: "" }),
        ]),
      ]),
      el(
        "tbody",
        {},
        docs.map((d) =>
          el("tr", {}, [
            el("td", { text: roleLabel(d.role || d.docType) }),
            el("td", { text: d.displayTitle || d.title || d.originalFilename || "—" }),
            el("td", { text: fmtDateShort(d.displayDate || d.documentDate) }),
            el("td", { text: executionLabel(d) }),
            el("td", {}, [
              isDocProcessing(d)
                ? ingestBar(el, d)
                : el("span", {
                    class: isOcrWeak(d)
                      ? "text-xs font-medium text-amber-800"
                      : "text-xs font-medium",
                    text: statusLabel(d),
                  }),
            ]),
            el("td", {}, [
              el("div", { class: "flex items-center gap-1 justify-end" }, [
                d.documentId && fetchJson
                  ? el("button", {
                      type: "button",
                      class: "lr-icon",
                      title: "Open PDF with highlights",
                      text: "📄",
                      onclick: () =>
                        openPassageViewer(el, {
                          documentId: d.documentId,
                          page: 1,
                          excerpt: "",
                          color: "yellow",
                          title: d.displayTitle || d.title || d.originalFilename,
                          apiUrl,
                          fetchJson,
                        }),
                    })
                  : d.documentId
                  ? el("a", {
                      class: "lr-icon",
                      href: docAnalysisHref(d.documentId),
                      target: "_blank",
                      title: "Open analysis",
                      text: "↗",
                    })
                  : null,
                d.documentId
                  ? el("a", {
                      class: "lr-icon",
                      href: docFileHref(apiUrl, d.documentId),
                      target: "_blank",
                      title: "Open file",
                      text: "📎",
                    })
                  : null,
                onUnlink
                  ? el("button", {
                      type: "button",
                      class: "lr-icon lr-icon-danger",
                      title: "Unlink",
                      text: "×",
                      onclick: () => onUnlink(d.documentId),
                    })
                  : null,
              ]),
            ]),
          ])
        )
      ),
    ]),
  ]);
}

function conditionTable(el, rows, { title, showAbsent = false } = {}) {
  const list = (rows || []).filter((r) => showAbsent || r.present);
  return el("div", { class: "lr-card p-4 space-y-2" }, [
    el("div", { class: "text-sm font-semibold text-gray-900", text: title }),
    !list.length
      ? el("div", {
          class: "text-sm text-gray-500",
          text: showAbsent ? "No rows." : "Nothing extracted for this block yet.",
        })
      : el("table", { class: "lr-table" }, [
          el("thead", {}, [
            el("tr", {}, [
              el("th", { text: "Condition" }),
              el("th", { text: "Snap" }),
              el("th", { text: "Note" }),
              el("th", { text: "" }),
            ]),
          ]),
          el(
            "tbody",
            {},
            list.map((r) => {
              const href = sourceHref(r.source);
              return el("tr", {}, [
                el("td", {}, [
                  el("div", { class: "font-medium", text: r.label || r.key }),
                ]),
                el("td", { class: "font-semibold lr-snap-cell", text: snapCell(r) }),
                el("td", { class: "lr-note", text: r.note || "" }),
                el("td", {}, [
                  href
                    ? el("a", {
                        href,
                        target: "_blank",
                        rel: "noopener",
                        class: "text-xs text-dae hover:underline",
                        title: "Open highlighted source",
                        text: "src ↗",
                      })
                    : null,
                ]),
              ]);
            })
          ),
        ]),
  ]);
}

function sourceLabel(source) {
  if (!source) return "";
  const bits = [];
  if (source.clause) bits.push(String(source.clause).slice(0, 40));
  if (source.page != null && source.page !== "") bits.push(`p.${source.page}`);
  else if (source.pageStart != null) bits.push(`p.${source.pageStart}`);
  if (!bits.length && source.documentId) bits.push(`#${source.documentId}`);
  return bits.join(" · ");
}

function groupLabels(report) {
  if (Array.isArray(report?.sectionGroups) && report.sectionGroups.length) {
    const seen = new Set();
    const mapped = [];
    for (const g of report.sectionGroups) {
      const id = canonicalGroup(g.id);
      if (seen.has(id)) continue;
      seen.add(id);
      const std = SECTION_GROUP_LABELS.find(([sid]) => sid === id);
      mapped.push([id, std ? std[1] : g.label]);
    }
    for (const [id, label] of SECTION_GROUP_LABELS) {
      if (!seen.has(id)) mapped.push([id, label]);
    }
    return mapped;
  }
  return SECTION_GROUP_LABELS;
}

function rowEffectivelyPresent(row) {
  if (!row?.present) return false;
  const { snap } = hygienizeSnap(row.key, row.snap || row.summary || "");
  return Boolean(String(snap || "").trim());
}

function alignStatus(cur, nxt, hasNext, { group } = {}) {
  const cOn = rowEffectivelyPresent(cur);
  const nOn = rowEffectivelyPresent(nxt);
  if (!hasNext) return cOn ? { label: "Stated", cls: "lr-align-same" } : { label: "Not found", cls: "lr-align-silent" };
  if (!cOn && !nOn) return { label: "Silent both", cls: "lr-align-silent" };
  // Economics terms always differ between leases — no Same/Gap verdict there.
  if (group === "economics") {
    if (cOn && nOn) return { label: "—", cls: "lr-align-silent" };
    if (cOn) return { label: "Return only", cls: "lr-align-silent" };
    return { label: "Next only", cls: "lr-align-silent" };
  }
  if (cOn && nOn) {
    const same = snapsLookSame(snapCell(cur), snapCell(nxt));
    // Same is the unremarkable case — flag only differences.
    return same
      ? { label: "", cls: "lr-align-same" }
      : { label: "Gap", cls: "lr-align-gap" };
  }
  if (cOn && !nOn) return { label: "Return only", cls: "lr-align-gap" };
  return { label: "Next only", cls: "lr-align-gap" };
}

function srcButton(el, source, { color, prefix, apiUrl, fetchJson, title }) {
  if (!source?.documentId || !apiUrl || !fetchJson) {
    const lab = sourceLabel(source);
    return lab ? el("span", { class: "text-xs text-gray-400 block", text: `${prefix}${lab}` }) : null;
  }
  const lab = sourceLabel(source);
  return el("button", {
    type: "button",
    class: "text-xs text-dae hover:underline block whitespace-nowrap text-left",
    title: source.excerpt || "Open highlighted passage",
    text: lab ? `${prefix}${lab}` : `${prefix}src`,
    onclick: () =>
      openPassageViewer(el, {
        documentId: source.documentId,
        page: source.page ?? source.pageStart ?? 1,
        excerpt: source.excerpt || source.quote || "",
        color,
        title: title || lab || `Document #${source.documentId}`,
        apiUrl,
        fetchJson,
      }),
  });
}

function isoToInputDate(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
}

function displayHouseDate(iso) {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(iso);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${Number(m[3])}-${months[Number(m[2]) - 1]}-${m[1]}`;
}

/** Settlement at Redelivery — parameters (as-of date + CTA) + deterministic table. */
function settlementPanel(el, report, { project, apiUrl, fetchJson, onImported } = {}) {
  const inputs = report?.trueupInputs || {};
  let settlement = report?.settlement || null;
  try {
    settlement = computeSettlement(inputs);
  } catch {
    /* keep stored */
  }
  const lines = settlement?.lines || [];
  const warnings = settlement?.warnings || [];
  const enabled = inputs.enableEolTrueup !== false && Boolean(inputs.ctaImport || inputs.components?.length || inputs.params?.length);

  async function persistPatch(patch) {
    if (!project?.id) return;
    try {
      const json = await fetchJson(apiUrl(`/api/ai/lease-returns/${project.id}/trueup-inputs`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      onImported?.(json.project);
    } catch (e) {
      alert(e?.message || String(e));
    }
  }

  const fileInput = el("input", {
    type: "file",
    accept: ".xlsx,.xls",
    class: "hidden",
    onchange: async () => {
      const file = fileInput.files?.[0];
      if (!file || !project?.id) return;
      const fd = new FormData();
      fd.append("file", file);
      try {
        const json = await fetchJson(apiUrl(`/api/ai/lease-returns/${project.id}/cta-import`), {
          method: "POST",
          body: fd,
        });
        onImported?.(json.project);
      } catch (e) {
        alert(e?.message || String(e));
      }
      fileInput.value = "";
    },
  });

  const enableChk = el("input", {
    type: "checkbox",
    checked: inputs.enableEolTrueup !== false && Boolean(inputs.components?.length || inputs.ctaImport),
    onchange: () => persistPatch({ enableEolTrueup: enableChk.checked }),
  });

  const asOfInput = el("input", {
    type: "date",
    class: "border border-gray-300 rounded px-2 py-1 text-sm",
    value: isoToInputDate(inputs.asOfDate || inputs.expiryDate),
    title: "Calculation as-of date — drives escalation / proration. CTA contract end is projection only.",
    onchange: () => {
      if (!asOfInput.value) return;
      persistPatch({ asOfDate: asOfInput.value, enableEolTrueup: true });
    },
  });

  const dirLabel = (d) => {
    if (d === "lessee_to_lessor") return "Lessee → Lessor";
    if (d === "lessor_to_lessee") return "Lessor → Lessee";
    if (d === "credit") return "Credit";
    if (d === "none") return "—";
    return d || "—";
  };

  const srcText = (s) => {
    if (!s) return "—";
    if (s === "MANUAL" || s.kind === "MANUAL") return "MANUAL";
    if (s.sheet) return `${s.sheet}${s.row != null ? ` r${s.row}` : ""}${s.kind === "projected" ? " · projected" : ""}`;
    if (s.doc || s.documentId) return `${s.doc || `#${s.documentId}`}${s.section ? ` ${s.section}` : ""}`;
    return "—";
  };

  const missingParams = [];
  if (!inputs.ctaImport && !(inputs.components || []).length) {
    missingParams.push("xRM Predict CTA export (.xlsx) — upload below");
  }
  if (!(inputs.params || []).some((p) => p.key === "rate" || p.key === "escalatedRate" || p.key === "baseYear")) {
    missingParams.push("Engine / component rates (from lease analysis settlement_params, or enter after Go)");
  }
  const engines = (inputs.components || []).filter((c) => c.group === "engine_prsv");
  for (const eng of engines) {
    if (eng.atReturn?.sinceSV == null) missingParams.push(`${eng.label}: Return Hrs SLSV (from CTA)`);
  }

  return el("div", { class: "lr-card lr-settle p-4" }, [
    el("div", { class: "flex flex-wrap items-center justify-between gap-2 mb-2" }, [
      el("h2", { text: "Settlement at Redelivery" }),
      el("div", { class: "flex items-center gap-2" }, [
        fileInput,
        el("button", {
          type: "button",
          class: "text-xs font-medium px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50",
          text: "Upload xRM Predict CTA (.xlsx)",
          onclick: () => fileInput.click(),
        }),
      ]),
    ]),

    el("div", { class: "lr-snap-block mb-3" }, [
      el("div", {
        class: "text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2",
        text: "Settlement parameters",
      }),
      el("div", { class: "flex flex-wrap items-end gap-4" }, [
        el("label", { class: "flex items-center gap-2 text-sm text-gray-800" }, [
          enableChk,
          el("span", { text: "Calculate EOL true-up" }),
        ]),
        el("label", { class: "flex flex-col gap-0.5 text-xs text-gray-600" }, [
          el("span", { text: "Calculate as of (editable)" }),
          asOfInput,
        ]),
        el("div", { class: "text-xs text-gray-500" }, [
          el("div", {
            text: `Projected contract end (CTA): ${displayHouseDate(inputs.expiryDate)}`,
          }),
          el("div", {
            text: `Delivery / start (CTA): ${displayHouseDate(inputs.deliveryDate)}`,
          }),
          el("div", {
            class: "mt-1",
            text: "Return CTA figures are projections. Changing as-of re-escalates rates and changes prorated amounts.",
          }),
        ]),
      ]),
      missingParams.length
        ? el("ul", { class: "mt-2 text-xs text-amber-800 list-disc pl-4" }, [
            el("li", {
              class: "list-none font-semibold pl-0 -ml-4 mb-1",
              text: "Required for a full calculation:",
            }),
            ...missingParams.slice(0, 8).map((t) => el("li", { text: t })),
          ])
        : null,
      engines.length
        ? el(
            "div",
            { class: "mt-2 text-xs text-gray-600" },
            [
              el("div", { class: "font-semibold text-gray-700 mb-1", text: "Technical status (from CTA — projected)" }),
              ...engines.map((eng) =>
                el("div", {
                  text: `${eng.label}${eng.serial ? ` · ESN ${eng.serial}` : ""}: Return ${eng.atReturn?.sinceSV ?? "—"} FH / ${eng.atReturn?.cyclesSinceSV ?? "—"} FC SLSV`,
                })
              ),
            ]
          )
        : null,
    ]),

    warnings.length
      ? el(
          "div",
          { class: "lr-settle-warn" },
          [
            el("div", { class: "font-semibold mb-1", text: "Warnings" }),
            el(
              "ul",
              {},
              warnings.map((w) => el("li", { text: w.text || w.code || "—" }))
            ),
          ]
        )
      : null,

    settlement?.enabled === false
      ? el("p", {
          class: "text-sm text-gray-500",
          text: "EOL true-up is off. Tick “Calculate EOL true-up” and set the as-of date.",
        })
      : lines.length
        ? el("table", { class: "lr-table lr-snap-matrix" }, [
            el("thead", {}, [
              el("tr", {}, [
                el("th", { text: "#" }),
                el("th", { text: "Item" }),
                el("th", { text: "Basis / formula" }),
                el("th", { text: "Amount" }),
                el("th", { text: "Direction" }),
                el("th", { text: "Source" }),
                el("th", { text: "Flags" }),
              ]),
            ]),
            el(
              "tbody",
              {},
              [
                ...lines.map((line) => {
                  const miss = line.missing || line.amount == null;
                  return el("tr", { class: miss ? "lr-row-silent" : "" }, [
                    el("td", { text: String(line.n || "") }),
                    el("td", { class: "font-medium", text: line.item || "—" }),
                    el("td", { class: "lr-note", text: line.basis || "—" }),
                    el("td", {
                      class: miss ? "lr-miss" : "lr-snap-cell",
                      text: miss ? "—" : fmtMoney(line.amount),
                    }),
                    el("td", { text: dirLabel(line.direction) }),
                    el("td", { class: "text-xs text-gray-600", text: srcText(line.source || line.techSource) }),
                    el("td", {
                      class: "text-xs",
                      text: (line.flags || []).join("; ") || "—",
                    }),
                  ]);
                }),
                el("tr", {}, [
                  el("td", {
                    colspan: "3",
                    class: "lr-net",
                    text: `Net cash at redelivery (as of ${displayHouseDate(settlement?.asOfDate || inputs.asOfDate)})`,
                  }),
                  el("td", {
                    class: "lr-net",
                    text: settlement?.netCash == null ? "—" : fmtMoney(settlement.netCash),
                  }),
                  el("td", {
                    colspan: "3",
                    class: "text-xs text-gray-500",
                    text:
                      (settlement?.unresolved || []).length
                        ? `Unresolved: ${settlement.unresolved.join("; ")}`
                        : "Positive = Lessee → Lessor",
                  }),
                ]),
              ]
            ),
          ])
        : el("p", {
            class: "text-sm text-gray-500",
            text: enabled
              ? "No calculable lines yet — upload the xRM Predict CTA export and run analysis for rates, or enter rates after Go."
              : "Upload an xRM Predict CTA export to load projected technical status, then set the as-of date.",
          }),
  ]);
}

/** Full both-ways matrix grouped by topic. Mandatory silent rows stay (collapsed). */
function snapMatrix(el, report, { currentOp, nextOp, project, apiUrl, fetchJson, silentExpanded, onSilentToggle } = {}) {
  const redelivery = injectCommercialRows(
    foldRows(report?.redelivery || []),
    report?.commercial,
    report?.header
  );
  const next = foldRows(report?.nextOperatorDelivery || []);
  const nextByKey = new Map(next.map((r) => [r.key, r]));
  const byKey = new Map(redelivery.map((r) => [r.key, r]));
  const keys = [];
  const seen = new Set();
  for (const r of [...redelivery, ...next]) {
    if (!r?.key || seen.has(r.key)) continue;
    keys.push(r.key);
    seen.add(r.key);
  }
  // Mandatory checklist keys are never dropped — silent vs Not applicable. stay visible
  for (const key of STD_KEY_ORDER) {
    if (seen.has(key)) continue;
    keys.push(key);
    seen.add(key);
  }
  const mode = compareModeOf(report, project);
  const hasNext = mode !== "none";
  const displayNext =
    nextOp || report?.header?.nextOperator || report?.aircraftBasics?.nextOperatorName || "Next";
  const nextHead = mode === "loi" ? `LOI · ${displayNext}` : `Next · ${displayNext}`;
  const locCur = byKey.get("location");
  const locNxt = nextByKey.get("location");
  const locationFilled = Boolean(locCur?.present || locNxt?.present);

  const visibleKeys = keys.filter((key) => {
    const cur = byKey.get(key) || {};
    const nxt = nextByKey.get(key) || {};
    const mandatory = STD_KEY_ORDER.includes(key);
    if (mandatory) {
      if (key === "eol_delivery_check" && locationFilled && !(cur.present || nxt.present)) return true;
      return true;
    }
    if (!cur.present && !nxt.present) return false;
    return true;
  });

  const headCells = [
    el("th", { text: "Condition" }),
    el("th", { text: currentOp ? `Return · ${currentOp}` : "Return" }),
  ];
  if (hasNext) headCells.push(el("th", { text: nextHead }));
  if (hasNext) headCells.push(el("th", { text: "Covered" }));
  headCells.push(el("th", { text: "Note" }), el("th", { text: "Src" }));

  const srcCell = (cur, nxt) => {
    const parts = [
      srcButton(el, cur.source, {
        color: "return",
        prefix: "R: ",
        apiUrl,
        fetchJson,
        title: cur.label,
      }),
    ];
    if (hasNext) {
      parts.push(
        srcButton(el, nxt.source, {
          color: "next",
          prefix: "N: ",
          apiUrl,
          fetchJson,
          title: nxt.label,
        })
      );
    }
    const shown = parts.filter(Boolean);
    return el("td", { class: "align-top" }, shown.length ? shown : [el("span", { class: "text-xs text-gray-300", text: "—" })]);
  };

  const labels = groupLabels(report);
  const knownGroups = new Set(labels.map(([id]) => id));
  const expanded = silentExpanded || new Set();

  const renderRow = (key) => {
    const cur = byKey.get(key) || { key, present: false };
    const nxt = nextByKey.get(key) || { key, present: false };
    const label = cur.label || nxt.label || key.replace(/_/g, " ");
    const note = combinedNote(cur, nxt, hasNext);
    const silentBoth = !cur.present && !nxt.present;
    const reason = cur.notFoundReason || nxt.notFoundReason || "";
    const align = alignStatus(cur, nxt, hasNext, { group: canonicalGroup(cur.group || nxt.group || "other") });
    const cells = [
      el("td", { class: "font-medium", text: label }),
      el("td", {
        class: silentBoth ? "lr-snap-cell lr-align-silent" : "lr-snap-cell",
        text: snapCell(cur, { silent: silentBoth }),
        title: silentBoth && reason ? reason : undefined,
      }),
    ];
    if (hasNext) {
      cells.push(
        el("td", {
          class: silentBoth ? "lr-snap-cell lr-align-silent" : "lr-snap-cell",
          text: snapCell(nxt, { silent: silentBoth }),
          title: silentBoth && reason ? reason : undefined,
        })
      );
      cells.push(el("td", { class: `lr-align ${align.cls}`, text: align.label }));
    }
    cells.push(el("td", { class: "lr-note", text: note || "—" }), srcCell(cur, nxt));
    return el("tr", { class: silentBoth ? "lr-row-silent" : "" }, cells);
  };

  return el("div", { class: "lr-card overflow-hidden" }, [
    el("table", { class: "lr-table lr-snap-matrix" }, [
      el("thead", {}, [el("tr", {}, headCells)]),
      el(
        "tbody",
        {},
        visibleKeys.length
          ? labels.flatMap(([gid, glabel]) => {
              const groupKeysFixed = visibleKeys.filter((key) => {
                const cur = byKey.get(key) || {};
                const nxt = nextByKey.get(key) || {};
                const g =
                  cur.group || nxt.group
                    ? canonicalGroup(cur.group || nxt.group)
                    : stdKeyGroup(key) || "other";
                if (gid === "other") return g === "other" || !knownGroups.has(g);
                return g === gid;
              });
              groupKeysFixed.sort((a, b) => {
                const ia = STD_KEY_ORDER.indexOf(a);
                const ib = STD_KEY_ORDER.indexOf(b);
                return (ia === -1 ? 500 : ia) - (ib === -1 ? 500 : ib);
              });
              if (!groupKeysFixed.length) return [];
              const filled = groupKeysFixed.filter((key) => {
                const cur = byKey.get(key) || {};
                const nxt = nextByKey.get(key) || {};
                return cur.present || nxt.present;
              });
              const silent = groupKeysFixed.filter((key) => !filled.includes(key));
              const colSpan = String(hasNext ? 6 : 4);
              const open = expanded.has(gid);
              const rows = [
                el("tr", { class: "lr-row-section" }, [el("td", { colspan: colSpan, text: glabel })]),
                ...filled.map(renderRow),
              ];
              if (silent.length) {
                rows.push(
                  el("tr", { class: "lr-row-silent-toggle" }, [
                    el(
                      "td",
                      {
                        colspan: colSpan,
                        class: "lr-silent-toggle",
                        text: open ? `Hide ${silent.length} silent` : `+ ${silent.length} silent`,
                        onclick: () => onSilentToggle?.(gid),
                      }
                    ),
                  ])
                );
                if (open) rows.push(...silent.map(renderRow));
              }
              return rows;
            })
          : [
              el("tr", {}, [
                el("td", {
                  colspan: String(hasNext ? 6 : 4),
                  class: "text-sm text-gray-500",
                  text: "No redelivery snaps yet — run the analysis from Setup.",
                }),
              ]),
            ]
      ),
    ]),
  ]);
}

/** Infer section group for a mandatory key when the report row is missing. */
function stdKeyGroup(key) {
  const map = {
    rent: "economics",
    security_deposit: "economics",
    chapter11: "economics",
    contribution_rent: "economics",
    rent_premium_acceleration: "economics",
    settled_payables: "economics",
    walkaway_date: "economics",
    delivery_cutoff: "economics",
    holdover_rent: "economics",
    covid_rent_deferral: "economics",
    reserve_modality: "reserves_eol",
    eol_type: "reserves_eol",
    eol_credit: "reserves_eol",
    maintenance_collateral: "reserves_eol",
    credit_test: "reserves_eol",
    rates_engines_prsv: "reserves_eol",
    rates_engines_llp: "reserves_eol",
    rates_lg: "reserves_eol",
    rates_apu: "reserves_eol",
    rates_propellers: "reserves_eol",
    rates_airframe: "reserves_eol",
    escalation_engines: "reserves_eol",
    escalation_airframe: "reserves_eol",
    eol_adjustments: "reserves_eol",
    ad_cost_sharing: "reserves_eol",
    mpd_clearance: "airframe",
    hard_times: "airframe",
    occm: "airframe",
    rotable_ageing: "airframe",
    engines_general: "engines",
    engines_llp: "engines",
    engines_bsi: "engines",
    engines_ecm: "engines",
    engine_oem_program: "engines",
    tripartite_cost_split: "engines",
    engine_reinstallation: "engines",
    landing_gear: "components",
    apu: "components",
    propellers: "components",
    tires_brakes: "components",
    general: "delivery",
    location: "delivery",
    eol_delivery_check: "delivery",
    ferry_flight: "delivery",
    demo_flight: "delivery",
    registration_deregistration: "delivery",
    delivery_schedule: "delivery",
    redelivery_schedule: "delivery",
    fuel_at_return: "delivery",
    standard: "delivery",
    modifications_stc: "delivery",
    repairs: "delivery",
    ad_clearance: "delivery",
    software: "delivery",
    foc_sb_kit: "delivery",
    storage_redelivery: "delivery",
    non_material_caps: "special",
    companion_cross_conditioning: "special",
    lessor_termination_option: "special",
    ber_replacement: "special",
    engine_sale_carveout: "special",
    records: "records",
    non_incident_statement: "records",
    paint: "records",
    placards_language: "records",
    cabin_softs: "records",
    related_swap: "other",
    pre_delivery_mods: "other",
  };
  return map[key] || null;
}

function supersessionBlock(el, report) {
  const s = report?.supersession;
  if (!s || (!s.summary && !(s.items || []).length && !(s.governingNow || []).length)) {
    return null;
  }
  const actionLabel = (a) => {
    const t = String(a || "").toLowerCase();
    if (t === "added") return "Added";
    if (t === "waived") return "Waived";
    if (t === "superseded") return "Superseded";
    if (t === "clarified") return "Clarified";
    return a || "—";
  };
  return el("div", { class: "lr-super" }, [
    el("h2", { text: "Superseding documents" }),
    s.summary ? el("p", { text: formatMoneyInText(s.summary) }) : null,
    (s.governingNow || []).length
      ? el(
          "p",
          {
            class: "text-sm text-gray-600 mb-3",
            text: `Governing now: ${s.governingNow
              .map((g) =>
                [g.title, g.date ? fmtDateShort(g.date) : null, formatMoneyInText(g.why)]
                  .filter(Boolean)
                  .join(" — ")
              )
              .join("; ")}`,
          }
        )
      : null,
    (s.items || []).length
      ? el("div", { class: "lr-card overflow-hidden" }, [
          el("table", { class: "lr-table" }, [
            el("thead", {}, [
              el("tr", {}, [
                el("th", { text: "Condition" }),
                el("th", { text: "Action" }),
                el("th", { text: "From" }),
                el("th", { text: "By" }),
                el("th", { text: "Substance" }),
              ]),
            ]),
            el(
              "tbody",
              {},
              s.items.map((it) =>
                el("tr", {}, [
                  el("td", { class: "font-medium", text: it.topic || "—" }),
                  el("td", { text: actionLabel(it.action) }),
                  el("td", { class: "text-xs text-gray-600", text: it.from || "—" }),
                  el("td", { class: "text-xs text-gray-600", text: it.by || "—" }),
                  el("td", { class: "lr-snap-cell", text: formatMoneyInText(it.summary) || "—" }),
                ])
              )
            ),
          ]),
        ])
      : el("p", {
          class: "text-sm text-gray-500",
          text: "No added, waived, or superseded conditions found in later documents.",
        }),
  ]);
}

function openProgressModal(el, { title = "Analyzing return conditions", onCancel } = {}) {
  const startedAt = Date.now();
  const stepEl = el("div", { class: "lr-step", text: "Starting analysis…" });
  const elapsedEl = el("div", { class: "lr-elapsed", text: "0:00 elapsed" });
  const logEl = el("div", { class: "lr-step-log" });
  const bar = el("div", { class: "lr-progress-bar" });
  const pctEl = el("div", { class: "text-xs text-gray-500 mt-2", text: "Working…" });
  const cancelBtn = onCancel
    ? el("button", {
        type: "button",
        class:
          "inline-flex items-center rounded-md border border-gray-200 bg-white text-xs font-medium px-2.5 py-1.5 text-gray-600 hover:bg-gray-50 mt-3",
        text: "Cancel analysis",
        onclick: async () => {
          if (!confirm("Stop this analysis? You can run it again afterwards.")) return;
          cancelBtn.disabled = true;
          cancelBtn.textContent = "Cancelling…";
          try {
            await onCancel();
          } catch {
            cancelBtn.disabled = false;
            cancelBtn.textContent = "Cancel analysis";
          }
        },
      })
    : null;
  const backdrop = el("div", { class: "lr-modal-backdrop", "data-lr-progress": "1" }, [
    el("div", { class: "lr-modal", role: "dialog", "aria-modal": "true" }, [
      el("div", { class: "lr-modal-h", text: title }),
      el("div", { class: "lr-modal-b" }, [
        el("div", { class: "lr-progress-track" }, [bar]),
        pctEl,
        stepEl,
        elapsedEl,
        logEl,
        el("p", {
          class: "text-xs text-gray-400 mt-3",
          text: "Reading the leases in batches (usually 3–6 minutes). The current step stays highlighted at the bottom.",
        }),
        cancelBtn,
      ]),
    ]),
  ]);
  document.querySelectorAll("[data-lr-progress]").forEach((n) => n.remove());
  document.body.appendChild(backdrop);

  const seen = new Set();
  const tick = setInterval(() => {
    const sec = Math.floor((Date.now() - startedAt) / 1000);
    const mm = Math.floor(sec / 60);
    const ss = String(sec % 60).padStart(2, "0");
    elapsedEl.textContent = `${mm}:${ss} elapsed · still working`;
  }, 1000);

  return {
    update({ message, current, total, events } = {}) {
      if (message) stepEl.textContent = message;
      if (current != null && total) {
        const pct = Math.max(5, Math.min(98, Math.round((Number(current) / Number(total)) * 100)));
        bar.classList.add("is-pct");
        bar.style.width = `${pct}%`;
        pctEl.textContent = `${pct}% · step ${current} of ${total}`;
      } else {
        bar.classList.remove("is-pct");
        bar.style.width = "32%";
        pctEl.textContent = "In progress…";
      }
      if (Array.isArray(events)) {
        const chronological = events
          .filter((ev) =>
            ["ANALYZE_PROGRESS", "ANALYZE_START", "ANALYZE_COMPLETE", "ANALYZE_FAILED"].includes(
              ev.eventType
            )
          )
          .slice()
          .sort((a, b) => {
            const ta = new Date(a.createdAt || a.created_at || 0).getTime();
            const tb = new Date(b.createdAt || b.created_at || 0).getTime();
            return ta - tb;
          })
          .filter((ev) => {
            const t = new Date(ev.createdAt || ev.created_at || 0).getTime();
            return !t || t >= startedAt - 5000;
          });
        for (const ev of chronological) {
          const key = `${ev.id || ev.createdAt}:${ev.message}`;
          if (seen.has(key)) continue;
          seen.add(key);
          logEl.querySelectorAll(".is-current").forEach((n) => n.classList.remove("is-current"));
          const line = el("div", { class: "is-current", text: ev.message || ev.eventType });
          logEl.appendChild(line);
          line.scrollIntoView({ block: "nearest" });
        }
        logEl.scrollTop = logEl.scrollHeight;
      }
    },
    close() {
      clearInterval(tick);
      backdrop.remove();
    },
  };
}

function renderList({ el, fetchJson, apiUrl, cleanText, host }) {
  host.replaceChildren(el("div", { class: "text-sm text-gray-500", text: "Loading projects…" }));

  async function load() {
    try {
      const data = await fetchJson(apiUrl("/api/ai/lease-returns"));
      const projects = data.projects || [];
      host.replaceChildren(
        chrome(el, {
          title: "Return conditions",
          actions: [
            el("a", {
              href: "#/figaro/lease-returns/new",
              class:
                "inline-flex items-center rounded-md bg-dae text-white text-sm font-medium px-3 py-1.5 hover:opacity-90",
              text: "New project",
            }),
          ],
        }),
        el("p", {
          class: "text-sm text-gray-600 mb-4",
          text: "MSN / operator projects that analyze lease delivery & redelivery conditions (integral of lease + amendments). Optional next-operator LOI/draft for alignment.",
        }),
        el("div", { class: "lr-card overflow-hidden" }, [
          projects.length
            ? el("table", { class: "lr-table" }, [
                el("thead", {}, [
                  el("tr", {}, [
                    el("th", { text: "MSN" }),
                    el("th", { text: "Operator" }),
                    el("th", { text: "Status" }),
                    el("th", { text: "Docs" }),
                    el("th", { text: "Report" }),
                    el("th", { text: "" }),
                  ]),
                ]),
                el(
                  "tbody",
                  {},
                  projects.map((p) =>
                    el("tr", {}, [
                      el("td", {}, [
                        el("a", {
                          href: `#/figaro/lease-returns/${p.id}`,
                          class: "font-semibold text-dae hover:underline",
                          text: p.msn || "—",
                        }),
                        p.registration
                          ? el("div", { class: "text-xs text-gray-400", text: p.registration })
                          : null,
                      ]),
                      el("td", { text: p.operatorName || "—" }),
                      el("td", {}, [
                        el("span", { class: `lr-status lr-status-${p.status}`, text: p.status || "—" }),
                      ]),
                      el("td", { text: String(p.docCount ?? "—") }),
                      el("td", {}, [
                        p.hasReport || p.reportGeneratedAt || p.status === "COMPLETE"
                          ? el("div", { class: "flex items-center gap-2" }, [
                              el("a", {
                                href: `#/figaro/lease-returns/${p.id}`,
                                class: "lr-icon",
                                title: "Open saved report (does not re-run analysis)",
                                text: "📄",
                              }),
                              el("span", {
                                class: "text-xs text-gray-500",
                                text:
                                  fmtDateTime(p.reportGeneratedAt || p.updatedAt) +
                                  ` (${fmtTokenCount(p.aiTokens)})`,
                                title:
                                  "Accumulated AI usage for this project (query + result tokens, all runs). Counting starts Aug 12, 2026 — earlier runs were not tagged.",
                              }),
                            ])
                          : el("span", { class: "text-xs text-gray-400", text: "—" }),
                      ]),
                      el("td", {}, [
                        el("button", {
                          type: "button",
                          class: "lr-icon lr-icon-danger",
                          title: "Delete project",
                          text: "×",
                          onclick: async () => {
                            if (!confirm(`Delete return-conditions project for MSN ${p.msn}?`)) return;
                            await fetchJson(apiUrl(`/api/ai/lease-returns/${p.id}`), {
                              method: "DELETE",
                            });
                            load();
                          },
                        }),
                      ]),
                    ])
                  )
                ),
              ])
            : el("div", {
                class: "p-6 text-sm text-gray-500",
                text: "No projects yet. Create one by picking an MSN that has lease docs in Figaro.",
              }),
        ])
      );
    } catch (e) {
      host.replaceChildren(
        el("div", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) })
      );
    }
  }
  load();
}

function renderNew({ el, fetchJson, apiUrl, cleanText, host }) {
  host.replaceChildren(el("div", { class: "text-sm text-gray-500", text: "Loading aircraft…" }));

  (async () => {
    try {
      const scope = await fetchJson(apiUrl("/api/ai/scope-options")).catch(() => ({
        operators: [],
        assets: [],
      }));
      const assets = (scope.assets || [])
        .filter((a) => String(a.assetType || "AIRFRAME") === "AIRFRAME")
        .slice()
        .sort((a, b) => String(a.serialNumber || "").localeCompare(String(b.serialNumber || "")));

      const msnFilter = el("input", {
        class: "w-full border border-gray-200 rounded-md px-3 py-2 text-sm",
        placeholder: "Filter MSN / registration / operator…",
      });
      const msnSelect = el("select", {
        class: "w-full border border-gray-200 rounded-md px-3 py-2 text-sm",
        size: 8,
      });
      const notesInput = el("textarea", {
        class: "w-full border border-gray-200 rounded-md px-3 py-2 text-sm",
        rows: "2",
        placeholder: "Notes (optional)",
      });
      const errBox = el("div", { class: "text-sm text-red-700 hidden" });
      let selectedAsset = null;

      function fillMsnOptions(filter = "") {
        const q = filter.trim().toLowerCase();
        const filtered = !q
          ? assets.slice(0, 400)
          : assets.filter((a) => assetLabel(a).toLowerCase().includes(q)).slice(0, 400);
        msnSelect.replaceChildren(
          el("option", { value: "", text: filtered.length ? "Select MSN…" : "No MSN match" }),
          ...filtered.map((a) => el("option", { value: String(a.id), text: assetLabel(a) }))
        );
      }
      fillMsnOptions();
      msnFilter.addEventListener("input", () => fillMsnOptions(msnFilter.value));
      msnSelect.addEventListener("change", () => {
        selectedAsset = assets.find((a) => String(a.id) === msnSelect.value) || null;
      });

      host.replaceChildren(
        chrome(el, { title: "New return-conditions project", crumbs: [{ label: "New" }] }),
        el("div", { class: "lr-card p-5 space-y-4 max-w-xl" }, [
          el("label", { class: "block space-y-1" }, [
            el("span", { class: "text-xs font-semibold text-gray-500 uppercase", text: "Filter" }),
            msnFilter,
          ]),
          el("label", { class: "block space-y-1" }, [
            el("span", {
              class: "text-xs font-semibold text-gray-500 uppercase",
              text: "MSN / airframe (brings operator)",
            }),
            msnSelect,
          ]),
          el("label", { class: "block space-y-1" }, [
            el("span", { class: "text-xs font-semibold text-gray-500 uppercase", text: "Notes" }),
            notesInput,
          ]),
          errBox,
          el("button", {
            type: "button",
            class:
              "inline-flex items-center rounded-md bg-dae text-white text-sm font-medium px-3 py-1.5",
            text: "Create & auto-link legal docs",
            onclick: async () => {
              errBox.classList.add("hidden");
              if (!selectedAsset) {
                errBox.textContent = "Select an MSN from the list.";
                errBox.classList.remove("hidden");
                return;
              }
              try {
                const json = await fetchJson(apiUrl("/api/ai/lease-returns"), {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    msn: selectedAsset.serialNumber || selectedAsset.msn,
                    assetId: selectedAsset.id,
                    aircraftId: selectedAsset.aircraftId || null,
                    operatorId: selectedAsset.operatorId || null,
                    registration: selectedAsset.metadata?.registration || null,
                    notes: notesInput.value.trim() || null,
                  }),
                });
                location.hash = `#/figaro/lease-returns/${json.project.id}`;
              } catch (e) {
                errBox.textContent = cleanText(e?.message) || String(e);
                errBox.classList.remove("hidden");
              }
            },
          }),
        ])
      );
    } catch (e) {
      host.replaceChildren(
        el("div", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) })
      );
    }
  })();
}

function renderDetail({ el, fetchJson, apiUrl, cleanText, host, id }) {
  host.replaceChildren(el("div", { class: "text-sm text-gray-500", text: "Loading project…" }));
  let project = null;
  let wizardStep = null;
  let userNavigated = false;
  let silentExpanded = new Set();
  let pollTimer = null;
  let progressUi = null;
  let operators = [];

  function stopPoll() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function needsPoll(p) {
    if (!p) return false;
    if (p.status === "RUNNING") return true;
    return (p.documents || []).some(isDocProcessing);
  }

  function ensurePoll() {
    stopPoll();
    if (!needsPoll(project)) return;
    const ms = project.status === "RUNNING" ? 1500 : 1200;
    pollTimer = setInterval(() => {
      reload({ quiet: true }).catch(() => {});
    }, ms);
  }

  function syncProgressFromProject() {
    if (!progressUi || !project) return;
    if (project.status === "RUNNING") {
      const prog = (project.events || []).find((e) => e.eventType === "ANALYZE_PROGRESS");
      const payload = prog?.payload && typeof prog.payload === "object" ? prog.payload : {};
      progressUi.update({
        message: prog?.message || "Analyzing return conditions…",
        current: payload.current,
        total: payload.total,
        events: project.events,
      });
      return;
    }
    if (project.status === "COMPLETE" || project.status === "FAILED") {
      progressUi.update({
        message: project.status === "COMPLETE" ? "Analysis complete" : project.lastError || "Failed",
        current: 5,
        total: 5,
        events: project.events,
      });
      const done = project.status;
      setTimeout(() => {
        progressUi?.close();
        progressUi = null;
        if (done === "COMPLETE") {
          if (!userNavigated) wizardStep = "C";
          paint();
        } else {
          paint();
        }
      }, 450);
    }
  }

  async function reload({ quiet = false } = {}) {
    const data = await fetchJson(apiUrl(`/api/ai/lease-returns/${id}`));
    const prevStatus = project?.status;
    project = data.project;
    if (project.status === "RUNNING" && !progressUi) {
      progressUi = openProgressModal(el, {
        title: `MSN ${project.msn} — return conditions`,
        onCancel: async () => {
          await fetchJson(apiUrl(`/api/ai/lease-returns/${id}/status`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: "FAILED", message: "Cancelled by user" }),
          });
          progressUi?.close();
          progressUi = null;
          await reload();
        },
      });
      progressUi.update({ message: "Analysis in progress…" });
    }
    syncProgressFromProject();
    if (!quiet || prevStatus !== project.status) paint();
    else if (!progressUi) paint();
    ensurePoll();
  }

  function paint() {
    const p = project;
    const report = p.report;
    const fileInput = el("input", {
      type: "file",
      class: "hidden",
      multiple: true,
      accept: ".pdf,.doc,.docx",
    });

    async function doUpload(files) {
      if (!files?.length) return;
      const prevDrop = drop.textContent;
      drop.textContent = files.length === 1 ? `Uploading ${files[0].name}…` : `Uploading ${files.length} files…`;
      drop.classList.add("is-drag");
      try {
        const fd = new FormData();
        for (const f of files) fd.append("files", f);
        const json = await fetchJson(apiUrl(`/api/ai/lease-returns/${id}/upload`), {
          method: "POST",
          body: fd,
        });
        const needsConfirm = (json.classifications || []).filter((c) => c.needsConfirmation);
        if (needsConfirm.length) {
          const lines = needsConfirm
            .map((c) => `${c.filename}: ${c.docType} (${Math.round((c.confidence || 0) * 100)}%) — ${c.reason || ""}`)
            .join("\n");
          alert(`Kind auto-detected with low confidence — use “Fix document kinds from titles” if wrong:\n\n${lines}`);
        }
        await reload();
      } catch (e) {
        drop.textContent = prevDrop;
        drop.classList.remove("is-drag");
        alert(cleanText(e?.message) || String(e));
      }
    }

    const drop = el("div", {
      class: "lr-drop",
      text: "Drop legal PDFs here — kind is detected from the filename",
      onclick: () => fileInput.click(),
      ondragover: (ev) => {
        ev.preventDefault();
        drop.classList.add("is-drag");
      },
      ondragleave: () => drop.classList.remove("is-drag"),
      ondrop: async (ev) => {
        ev.preventDefault();
        drop.classList.remove("is-drag");
        await doUpload([...ev.dataTransfer.files]);
      },
    });
    fileInput.onchange = async () => {
      await doUpload([...fileInput.files]);
      fileInput.value = "";
    };

    const goDisabled = !(p.readiness?.ready) || p.status === "RUNNING";
    const goBtn = el("button", {
      type: "button",
      class: `inline-flex items-center rounded-md text-sm font-medium px-3 py-1.5 shadow-sm ${
        goDisabled ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-dae text-white hover:opacity-90"
      }`,
      text:
        p.status === "RUNNING"
          ? "Analyzing…"
          : p.report
            ? "Re-run analysis"
            : "Run analysis",
      disabled: goDisabled,
      onclick: async () => {
        goBtn.disabled = true;
        goBtn.textContent = "Analyzing…";
        userNavigated = false;
        if (!progressUi) {
          progressUi = openProgressModal(el, {
            title: `MSN ${p.msn} — return conditions`,
            onCancel: async () => {
              await fetchJson(apiUrl(`/api/ai/lease-returns/${id}/status`), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status: "FAILED", message: "Cancelled by user" }),
              });
              progressUi?.close();
              progressUi = null;
              await reload();
            },
          });
        }
        progressUi.update({ message: "Starting analysis…" });
        try {
          await fetchJson(apiUrl(`/api/ai/lease-returns/${id}/analyze`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: "{}",
          });
          progressUi.update({ message: "Queued — gathering lease excerpts…" });
        } catch (e) {
          // Keep modal open: old sync path 504s while work may continue; new path returns fast.
          progressUi.update({
            message: `Waiting for server… ${cleanText(e?.message) || "retrying"}`,
          });
        }
        ensurePoll();
        try {
          await reload({ quiet: false });
        } catch {
          /* poll will retry */
        }
      },
    });

    const resetBtn =
      p.status === "RUNNING"
        ? el("button", {
            type: "button",
            class:
              "inline-flex items-center rounded-md border border-gray-200 bg-white text-xs font-medium px-2.5 py-1.5 text-gray-700 hover:bg-gray-50",
            text: "Reset stuck job",
            onclick: async () => {
              if (!confirm("Mark this analysis as failed so you can run Go again?")) return;
              await fetchJson(apiUrl(`/api/ai/lease-returns/${id}/status`), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status: "FAILED", message: "Reset by user" }),
              });
              progressUi?.close();
              progressUi = null;
              await reload();
            },
          })
        : null;

    function buildOperatorSelect({ valueId, valueName, blankLabel }) {
      const sel = el("select", {
        class: "w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm",
      });
      const opts = [el("option", { value: "", text: blankLabel })];
      let matched = false;
      for (const o of operators) {
        const selected =
          (valueId && Number(o.id) === Number(valueId)) ||
          (!valueId && valueName && String(o.name).toLowerCase() === String(valueName).toLowerCase());
        if (selected) matched = true;
        opts.push(
          el("option", {
            value: String(o.id),
            text: o.name,
            selected,
          })
        );
      }
      // Free-text / lease-inferred name not in the fleet list
      if (valueName && !matched) {
        opts.splice(
          1,
          0,
          el("option", {
            value: `name:${valueName}`,
            text: `${valueName} (from lease / docs)`,
            selected: true,
          })
        );
      }
      sel.replaceChildren(...opts);
      sel.addEventListener("change", () => {
        saveOperators().catch((e) => alert(cleanText(e?.message) || String(e)));
      });
      return sel;
    }

    const opSelect = buildOperatorSelect({
      valueId: p.operatorId,
      valueName: p.operatorName,
      blankLabel: "Select current (outgoing) operator…",
    });
    const nextOpSelect = buildOperatorSelect({
      valueId: p.nextOperatorId,
      valueName: p.nextOperatorName,
      blankLabel: "Select next (incoming) operator — or leave blank…",
    });

    async function saveOperators() {
      const body = { currentOperatorConfirmed: true };
      const curVal = opSelect.value;
      if (curVal.startsWith("name:")) {
        body.operatorName = curVal.slice(5);
      } else if (curVal) {
        body.operatorId = Number(curVal);
        const opt = operators.find((o) => Number(o.id) === Number(curVal));
        if (opt?.name) body.operatorName = opt.name;
      } else {
        body.operatorName = null;
        body.operatorId = null;
      }
      const nextVal = nextOpSelect.value;
      if (nextVal.startsWith("name:")) {
        body.nextOperatorName = nextVal.slice(5);
        body.nextOperatorConfirmed = true;
      } else if (nextVal) {
        body.nextOperatorId = Number(nextVal);
        const opt = operators.find((o) => Number(o.id) === Number(nextVal));
        if (opt?.name) body.nextOperatorName = opt.name;
        body.nextOperatorConfirmed = true;
      } else {
        body.nextOperatorName = null;
        body.nextOperatorId = null;
      }
      await fetchJson(apiUrl(`/api/ai/lease-returns/${id}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      await reload();
    }

    if (!wizardStep) {
      const snapsOk = report && (report.redelivery || []).some((r) => r.present);
      wizardStep = snapsOk ? "C" : "A";
    }

    const actBtn = (text, onclick) =>
      el("button", {
        type: "button",
        class:
          "inline-flex items-center rounded-md border border-gray-200 bg-white text-xs font-medium px-2.5 py-1 hover:bg-gray-50",
        text,
        onclick,
      });

    const rescan = async () => {
      await fetchJson(apiUrl(`/api/ai/lease-returns/${id}/auto-link`), { method: "POST", body: "{}" });
      await reload();
    };
    const fixKinds = async () => {
      await fetchJson(apiUrl(`/api/ai/lease-returns/${id}/fix-roles`), { method: "POST", body: "{}" });
      await reload();
    };
    const reocr = async () => {
      const r = await fetchJson(apiUrl(`/api/ai/lease-returns/${id}/reocr-weak`), {
        method: "POST",
        body: "{}",
      });
      const n = r.queued?.length || 0;
      alert(n ? `Re-queued ${n} document(s). Wait until status is INDEXED.` : r.message || "Nothing to re-OCR");
      await reload();
    };
    const confirmNone = async () => {
      await fetchJson(apiUrl(`/api/ai/lease-returns/${id}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amendmentsNoneConfirmed: true }),
      });
      await reload();
    };
    const confirmNextNone = async () => {
      await fetchJson(apiUrl(`/api/ai/lease-returns/${id}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nextNoneConfirmed: true }),
      });
      await reload();
    };

    const counts = p.readiness?.counts || {};
    const settings = p.settings || {};
    const dash = el("span", { class: "text-xs text-gray-300", text: "—" });
    const actionRow = (...nodes) => {
      const bits = nodes.filter(Boolean);
      if (!bits.length) return dash;
      return el("div", { class: "flex flex-wrap gap-1 justify-end" }, bits);
    };
    const actionsFor = (c) => {
      if (c.id === "lease_present" && !c.ok) {
        return actionRow(
          actBtn("Upload", () => fileInput.click()),
          actBtn("Scan library", () => rescan().catch((e) => alert(cleanText(e?.message) || String(e))))
        );
      }
      if ((c.id === "lease_indexed" || c.id === "text_quality") && p.readiness?.weakDocs?.length) {
        return actionRow(actBtn("Re-OCR", () => reocr().catch((e) => alert(cleanText(e?.message) || String(e)))));
      }
      if (c.id === "amendments_visible" && !(counts.amendment > 0) && !settings.amendmentsNoneConfirmed) {
        return actionRow(
          actBtn("Upload", () => fileInput.click()),
          actBtn("Confirm none", () => confirmNone().catch((e) => alert(cleanText(e?.message) || String(e))))
        );
      }
      if (
        c.id === "next_operator" &&
        !(counts.nextLoi > 0 || counts.nextDraft > 0) &&
        !settings.nextNoneConfirmed
      ) {
        return actionRow(
          actBtn("Upload", () => fileInput.click()),
          actBtn("Mark none", () => confirmNextNone().catch((e) => alert(cleanText(e?.message) || String(e))))
        );
      }
      if (c.id === "other_legal" && c.optional && !(counts.otherLegal > 0)) {
        return actionRow(actBtn("Upload", () => fileInput.click()));
      }
      return dash;
    };

    const stageBtn = (letter, title, hint, key) =>
      el(
        "button",
        {
          type: "button",
          class: `lr-stage-btn${wizardStep === key ? " is-on" : ""}`,
          onclick: () => {
            userNavigated = true;
            wizardStep = key;
            paint();
          },
        },
        [
          el("div", { class: "lr-stage-letter", text: letter }),
          el("div", { class: "lr-stage-title", text: title }),
          el("div", { class: "lr-stage-hint", text: hint }),
        ]
      );

    const opUnrecognized =
      p.operatorName &&
      !operators.some((o) => String(o.name).toLowerCase() === String(p.operatorName).toLowerCase());

    const mode = compareModeOf(report, p);

    const panelA = el("div", { class: "space-y-4" }, [
      el("div", { class: "lr-card p-4 space-y-3" }, [
        el("div", { class: "flex flex-wrap items-center justify-between gap-2" }, [
          el("div", { class: "text-sm font-semibold", text: "Documents" }),
          el("div", { class: "flex items-center gap-2" }, [
            actBtn("Scan library", () => rescan().catch((e) => alert(cleanText(e?.message) || String(e)))),
            actBtn("Fix kinds", () => fixKinds().catch((e) => alert(cleanText(e?.message) || String(e)))),
            el("button", {
              type: "button",
              class: "lr-plus",
              title: "Upload",
              text: "+",
              onclick: () => fileInput.click(),
            }),
            fileInput,
          ]),
        ]),
        drop,
        parsingBanner(el, p.documents),
        legalTable(el, p.documents, {
          apiUrl,
          fetchJson,
          onUnlink: async (documentId) => {
            await fetchJson(apiUrl(`/api/ai/lease-returns/${id}/documents/${documentId}`), {
              method: "DELETE",
            });
            await reload();
          },
        }),
      ]),
      el("div", { class: "lr-card p-4 space-y-3" }, [
        el("div", { class: "text-sm font-semibold", text: "Operators" }),
        opUnrecognized
          ? el("p", {
              class: "text-sm text-gray-600",
              text: "Current operator was not found in the fleet list — confirm or pick the right name before analysis.",
            })
          : el("p", {
              class: "text-sm text-gray-600",
              text: "Current operator comes from the MSN. Leave next blank if there is no incoming operator (no compare). An LOI can be compared with less detail than a lease.",
            }),
        el("div", { class: "grid sm:grid-cols-2 gap-3" }, [
          el("label", { class: "block space-y-1" }, [
            el("span", { class: "text-xs text-gray-500", text: "Current (outgoing)" }),
            opSelect,
          ]),
          el("label", { class: "block space-y-1" }, [
            el("span", { class: "text-xs text-gray-500", text: "Next (incoming) — optional" }),
            nextOpSelect,
          ]),
        ]),
      ]),
      el("div", { class: "lr-card p-4 space-y-3" }, [
        el("div", { class: "text-sm font-semibold", text: "Readiness" }),
        el("div", { class: "overflow-x-auto" }, [
          el("table", { class: "lr-table" }, [
            el("thead", {}, [
              el("tr", {}, [
                el("th", { text: "Item" }),
                el("th", { text: "Status" }),
                el("th", { text: "Detail" }),
                el("th", { text: "Action" }),
              ]),
            ]),
            el(
              "tbody",
              {},
              (p.readiness?.checks || []).map((c) => {
                const tone = readinessTone(c);
                return el("tr", {}, [
                  el("td", { class: "font-medium", text: c.label }),
                  el("td", { class: tone.cls, text: tone.label }),
                  el("td", { class: "text-xs text-gray-600", text: c.detail || "" }),
                  el("td", {}, [actionsFor(c)]),
                ]);
              })
            ),
          ]),
        ]),
      ]),
      p.readiness?.ready
        ? el("div", { class: "lr-card p-4 space-y-2" }, [
            el("p", {
              class: "text-sm text-gray-600",
              text: `MSN ${p.msn || "—"} is ready. The analysis runs once and the report is saved — opening it later does not re-run it.`,
            }),
            el("div", { class: "flex flex-wrap items-center gap-2" }, [goBtn, resetBtn]),
          ])
        : el("p", {
            class: "text-sm text-gray-600",
            text: "Finish the items that need action, then run the analysis.",
          }),
    ]);

    const panelC = report
      ? el("div", { class: "space-y-4" }, [
          el("div", { class: "lr-card p-4 space-y-3" }, [
            el("div", { class: "flex flex-wrap items-center justify-between gap-2" }, [
              el("div", { class: "text-sm font-semibold", text: "Report" }),
              report.generatedAt
                ? el("div", {
                    class: "text-xs text-gray-500",
                    text: `Created ${fmtDateTime(report.generatedAt)}`,
                  })
                : null,
            ]),
            disclaimerEl(el),
            reportMethodBanner(el, report),
            aircraftSnapshotBlock(el, { project: p, report }),
            settlementPanel(el, report, {
              project: p,
              apiUrl,
              fetchJson,
              onImported: (next) => {
                if (next) {
                  project = next;
                  paint();
                }
              },
            }),
            mode === "loi"
              ? el("p", {
                  class: "text-sm text-gray-600",
                  text: "Compared to a letter of intent — fewer details than a lease are expected.",
                })
              : mode === "none"
                ? el("p", {
                    class: "text-sm text-gray-600",
                    text: "No next operator — return conditions only, no comparison.",
                  })
                : null,
            snapMatrix(el, report, {
              currentOp: p.operatorName,
              nextOp: p.nextOperatorName,
              project: p,
              apiUrl,
              fetchJson,
              silentExpanded,
              onSilentToggle: (gid) => {
                if (silentExpanded.has(gid)) silentExpanded.delete(gid);
                else silentExpanded.add(gid);
                paint();
              },
            }),
            supersessionBlock(el, report),
            documentsUsedBlock(el, governingDocsForDisplay(report, p), { apiUrl, fetchJson }),
          ]),
        ])
      : el("div", {
          class: "lr-card p-5 text-sm text-gray-600",
          text: "No report yet — run the analysis from Setup.",
        });

    host.replaceChildren(
      el("div", { class: "lr-actionbar lr-no-print" }, [
        el("div", { class: "lr-actionbar-inner" }, [
          el("div", { class: "min-w-0" }, [
            el("div", {
              class: "text-sm font-semibold text-gray-900 truncate",
              text: `MSN ${p.msn || "—"} · ${p.operatorName || "Operator"}`,
            }),
            el("div", {
              class: "text-xs text-gray-500",
              text:
                p.status === "RUNNING"
                  ? "Analysis running"
                  : wizardStep === "C"
                    ? "Report"
                    : "Setup",
            }),
          ]),
          el("div", { class: "flex flex-wrap items-center gap-2" }, [
            el("button", {
              type: "button",
              class: "lr-icon lr-icon-danger",
              title: "Delete project",
              text: "×",
              onclick: async () => {
                if (!confirm("Delete this project?")) return;
                stopPoll();
                progressUi?.close();
                await fetchJson(apiUrl(`/api/ai/lease-returns/${id}`), { method: "DELETE" });
                location.hash = "#/figaro/lease-returns";
              },
            }),
            report
              ? el("button", {
                  type: "button",
                  class:
                    "inline-flex items-center rounded-md border border-gray-200 bg-white text-sm font-medium px-3 py-1.5 hover:bg-gray-50",
                  text: "Report",
                  onclick: () => {
                    wizardStep = "C";
                    paint();
                  },
                })
              : null,
          ]),
        ]),
      ]),
      el("div", { class: "lr-detail-body space-y-4" }, [
        el("div", { class: "lr-stage-rail" }, [
          stageBtn("A", "Setup", "Documents, operators, readiness — then run", "A"),
          stageBtn("B", "Report", "Return conditions", "C"),
        ]),
        wizardStep === "C" ? panelC : panelA,
      ])
    );
  }

  fetchJson(apiUrl("/api/ai/scope-options"))
    .then((scope) => {
      operators = scope.operators || [];
    })
    .catch(() => {
      operators = [];
    })
    .finally(() => {
      reload().catch((e) => {
        host.replaceChildren(
          el("div", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) })
        );
      });
    });
}

export function renderLeaseReturnsRoute({ el, fetchJson, apiUrl, cleanText, routeKey }) {
  ensureStyles();
  const host = el("div", { class: "space-y-4" });
  const raw = String(routeKey || "").replace(/^figaro\/lease-returns\/?/, "").split("?")[0];
  const reportMatch = raw.match(/^(\d+)\/report$/);
  if (reportMatch && typeof window !== "undefined") {
    history.replaceState(null, "", `#/figaro/lease-returns/${reportMatch[1]}`);
  }
  const idRaw = reportMatch ? reportMatch[1] : raw;
  if (!raw || raw === "") renderList({ el, fetchJson, apiUrl, cleanText, host });
  else if (raw === "new") renderNew({ el, fetchJson, apiUrl, cleanText, host });
  else if (/^\d+$/.test(idRaw)) renderDetail({ el, fetchJson, apiUrl, cleanText, host, id: Number(idRaw) });
  else renderList({ el, fetchJson, apiUrl, cleanText, host });
  return host;
}
